// src/components/sections/About.jsx
// Company info - light theme, centered

import React from 'react';
import useInView from '../../hooks/useInView';

const CAPABILITIES = [
  { label: 'GPS Telemetry', number: '01' },
  { label: 'Fleet Analytics', number: '02' },
  { label: 'SARS Compliance', number: '03' },
  { label: 'API Integration', number: '04' },
];

const About = () => {
  const [ref, inView] = useInView();

  return (
    <section id="about" className="py-16 md:py-32 relative overflow-hidden bg-white/60 backdrop-blur-sm">
      {/* Background pattern */}
      <div
        className="absolute inset-0 opacity-[0.03] pointer-events-none"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%230d9488' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
        }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div
          ref={ref}
          className={`flex flex-col items-center text-center transition-all duration-700 ${
            inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          {/* Hexagon diagram */}
          <div className="w-72 md:w-96 aspect-square mx-auto relative mb-12">
            <svg viewBox="0 0 400 400" className="w-full h-full">
              {/* Center hex */}
              <polygon points="200,60 290,110 290,210 200,260 110,210 110,110" fill="rgba(13,148,136,0.15)" stroke="rgba(13,148,136,0.5)" strokeWidth="1.5" />
              {/* Top hex */}
              <polygon points="200,0 260,30 260,90 200,120 140,90 140,30" fill="rgba(13,148,136,0.08)" stroke="rgba(13,148,136,0.3)" strokeWidth="1" />
              {/* Bottom left */}
              <polygon points="120,200 180,230 180,290 120,320 60,290 60,230" fill="rgba(13,148,136,0.08)" stroke="rgba(13,148,136,0.3)" strokeWidth="1" />
              {/* Bottom right */}
              <polygon points="280,200 340,230 340,290 280,320 220,290 220,230" fill="rgba(13,148,136,0.08)" stroke="rgba(13,148,136,0.3)" strokeWidth="1" />
              {/* Connecting lines */}
              <line x1="200" y1="120" x2="200" y2="160" stroke="rgba(13,148,136,0.35)" strokeWidth="1" strokeDasharray="4,4" />
              <line x1="150" y1="220" x2="120" y2="230" stroke="rgba(13,148,136,0.35)" strokeWidth="1" strokeDasharray="4,4" />
              <line x1="250" y1="220" x2="280" y2="230" stroke="rgba(13,148,136,0.35)" strokeWidth="1" strokeDasharray="4,4" />
              {/* Labels */}
              <text x="200" y="170" textAnchor="middle" fill="rgba(13,148,136,0.9)" fontSize="14" fontWeight="600" fontFamily="Comfortaa, sans-serif">GPS TECH</text>
              <text x="200" y="190" textAnchor="middle" fill="rgba(13,148,136,0.5)" fontSize="10" fontFamily="Noto Sans, sans-serif">Holdings</text>
              <text x="200" y="65" textAnchor="middle" fill="rgba(63,72,84,0.8)" fontSize="10" fontFamily="Noto Sans, sans-serif">TripData</text>
              <text x="120" y="270" textAnchor="middle" fill="rgba(63,72,84,0.8)" fontSize="10" fontFamily="Noto Sans, sans-serif">GeoDrive</text>
              <text x="280" y="270" textAnchor="middle" fill="rgba(63,72,84,0.8)" fontSize="10" fontFamily="Noto Sans, sans-serif">GeoFleet</text>
              {/* Pulse center */}
              <circle cx="200" cy="160" r="3" fill="rgba(13,148,136,0.7)">
                <animate attributeName="r" values="3;5;3" dur="2s" repeatCount="indefinite" />
                <animate attributeName="opacity" values="0.7;0.3;0.7" dur="2s" repeatCount="indefinite" />
              </circle>
            </svg>
          </div>

          {/* Text */}
          <div className="max-w-2xl">
            <h2 className="text-3xl md:text-4xl font-display font-bold tracking-tight mb-6 text-gt-dark-100">
              Founded in{' '}
              <span className="text-gradient">South Africa</span>
              <br />built for the world
            </h2>
            <p className="text-gt-dark-400 leading-relaxed mb-6">
              GPS Tech is a technology company founded in South Africa, building
              telemetry platforms for fleet operators, businesses, and individuals
              who need reliable vehicle tracking and compliance tools — wherever they operate.
            </p>
            <p className="text-gt-dark-400 leading-relaxed mb-8">
              Our platforms are built with real-world requirements in mind — from
              SARS logbook compliance to multi-currency support, resilient architecture,
              and integration with GPS hardware deployed globally.
            </p>

            {/* Capabilities grid */}
            <div className="grid grid-cols-2 gap-4 max-w-md mx-auto">
              {CAPABILITIES.map((item, i) => (
                <div
                  key={i}
                  className="flex items-center justify-center gap-3 p-3 rounded-xl bg-gt-silver-50 border border-gt-silver-200"
                >
                  <span className="text-xs font-mono font-bold text-teal-600">{item.number}</span>
                  <span className="text-sm text-gt-dark-300">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default About;